
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Download, RefreshCw } from 'lucide-react';
import { Payment } from '@/services/types';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface PaymentsTableProps {
  payments: Payment[];
  loading: boolean;
  onRefresh?: () => void;
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({ 
  payments, 
  loading, 
  onRefresh 
}) => {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error, dateString);
      return 'Некорректная дата';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return <Badge variant="default" className="bg-green-500">Оплачено</Badge>;
      case 'pending':
        return <Badge variant="secondary">Ожидает</Badge>;
      case 'error':
      case 'failed':
        return <Badge variant="destructive">Ошибка</Badge>;
      case 'auth':
        return <Badge variant="outline">Авторизация</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMethodText = (method: string) => {
    switch (method) {
      case 'card': return 'Банковская карта';
      case 'cash': return 'Наличные';
      case 'account': return 'Счет';
      case 'sbp': return 'СБП';
      default: return method;
    }
  };

  const handleOpenReceipt = (receiptUrl: string | undefined) => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  const handleOpenInvoice = (paymentNumber: string | undefined) => {
    if (!paymentNumber) return;
    
    const invoiceUrl = `https://sp1-nova.ru/api/pay/?subdomain=tuktukrent.amocrm.ru&invoice_id=${paymentNumber}`;
    window.open(invoiceUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        <span>Загрузка платежей...</span>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">Платежи не найдены</p>
        {onRefresh && (
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Обновить
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onRefresh && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Обновить
          </Button>
        </div>
      )}
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Дата</TableHead>
            <TableHead>Номер аренды</TableHead>
            <TableHead>Сумма</TableHead>
            <TableHead>Способ оплаты</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead className="text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>
                {formatDate(payment.date)}
              </TableCell>
              <TableCell>
                {payment.rentalHumanNumber ? (
                  <span className="font-medium text-primary">
                    {payment.rentalHumanNumber}
                  </span>
                ) : payment.human_number ? (
                  <span className="font-medium">
                    {payment.human_number}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    #{payment.rentalId.substring(0, 8)}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <span className="font-medium">
                  {payment.amount.toLocaleString()} ₽
                </span>
              </TableCell>
              <TableCell>
                {getMethodText(payment.method)}
              </TableCell>
              <TableCell>
                {getStatusBadge(payment.status)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end space-x-2">
                  {(payment.status === 'paid' || payment.status === 'completed') && payment.receiptUrl && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleOpenReceipt(payment.receiptUrl)}
                      title="Скачать чек"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                  {payment.status === 'pending' && payment.human_number && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleOpenInvoice(payment.human_number)}
                      title="Оплатить"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                  {payment.human_number && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleOpenInvoice(payment.human_number)}
                      title="Открыть счет"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
